<template>
    <div id="rq-profile-info" class="d-flex flex-column">
        <div class="d-flex flex-column align-items-center">
            <Avatar :image="this.avatar" />
            <span style="margin-top:7px" v-rq-tooltip="{ html: true, trigger:'hover', title: 'Change Photo' }">
                <button
                    ref="photoButton"
                    style="width:150px"
                    automation_id="btn_dm_modal_photo"
                    type="button"
                    class="btn btn-theme"
                    :disabled="false"
                    @click="toggleAvatarPopup">
                    Change Photo
                </button>
            </span>
            <span style="margin-top:7px" v-rq-tooltip="{ html: true, trigger:'hover', title: 'Remove Photo' }">
                <button
                    ref="removeButton"
                    style="width:150px"
                    automation_id="btn_dm_modal_remove"
                    type="button"
                    class="btn btn-theme"
                    :disabled="false"
                    @click="removeAvatar">
                    Remove Photo
                </button>
            </span>
            <span style="margin-top:7px" v-rq-tooltip="{ html: true, trigger:'hover', title: 'Change Password' }">
                <button
                    ref="okButton"
                    style="width:150px"
                    automation_id="btn_dm_modal_ok"
                    type="button"
                    class="btn btn-theme"
                    :disabled="false"
                    @click="togglePasswordChangePopup">
                    Change Password
                </button>
            </span>
        </div>
        <rq-page-section title="Notification Preferences" >
            <b-form-checkbox
                class="rq-profile-email"
                automation_id="chk_proinfo_email"
                @change="onFieldChange"
                v-model="localInfo.notificationEmail">
                Email
            </b-form-checkbox>
            <b-form-checkbox
                class="rq-profile-text"
                automation_id="chk_proinfo_text"
                @change="onFieldChange"
                v-model="localInfo.notificationSms">
                Text / SMS
            </b-form-checkbox>
        </rq-page-section>
        <div class="row">
            <div class="col col-12 form-group form-required" :class="{ 'has-error':v$.localInfo.cellPhone.$error}">
                <label for="cellPhone">Mobile Phone</label>
                <rq-masked-input
                    id="cellPhone"
                    placeholder="(999) 999-9999"
                    maskType="phone"
                    isMasked="true"
                    v-model="v$.localInfo.cellPhone.$model"
                />
                <rq-validation-feedback
                    :container="tooltipContainer"
                    :boundary="tooltipContainer"
                    :messages="{
                        'Mobile Phone is required.': v$.localInfo.cellPhone.required,
                        'Invalid Phone#': !v$.localInfo.cellPhone.validFormat
                    }"
                />
            </div>
        </div>
        <div class="row">
            <div class="col col-12 form-group" :class="{ 'has-error':v$.localInfo.mfaPhoneNumber.$error}">
                <label for="mfaPhoneNumber">MFA Mobile Phone</label>
                <rq-masked-input id="mfaPhoneNumber"
                    placeholder="(999) 999-9999"
                    maskType="phone"
                    isMasked="true"
                    v-model="v$.localInfo.mfaPhoneNumber.$model"
                />
                <rq-validation-feedback
                    :container="tooltipContainer"
                    :boundary="tooltipContainer"
                    :messages="{
                        'Invalid Phone#': !v$.localInfo.mfaPhoneNumber.validFormat
                    }"
                />
            </div>
        </div>
        <template v-if="this.avatarPopupOpen">
            <AvatarEditPopup @Close="toggleAvatarPopup" @Updated="onUpdateAvatar" :userAccountId="userAccountId" />
        </template>
        <template v-if="this.passwordChangePopupOpen">
            <PasswordManagement
                @Close="togglePasswordChangePopup"
                @Success="togglePasswordChangePopup"
                :emailAddress="email" />
        </template>
    </div>
</template>
<script>
import RegEx from "@/shared/utilities/RegEx";
import { UserAccountProfile } from "./models";
import { useVuelidate } from "@vuelidate/core";
import { required } from '@vuelidate/validators';
import AvatarEditPopup from "@/modules/consumer/profilemanagement/AvatarEditPopup";
import Avatar from '@/components/Avatar';
import PasswordManagement from "@/modules/consumer/profilemanagement/PasswordManagement";

export default {
    components: {
        Avatar,
        AvatarEditPopup,
        PasswordManagement,
    },
    props: {
        info: {
            type: Object
        },
    },
    setup: () => ({ v$: useVuelidate() }),
    data: function() {
        return {
            localInfo: new UserAccountProfile(),
            avatarPopupOpen: false,
            passwordChangePopupOpen: false,
            avatar: null,
        }
    },
    computed: {
        tooltipContainer() {
            return `#${this.$parent.$parent.$parent.dialogId}`;
        },
        isValid() {
            return !this.v$.$error;
        },
        userAccountId() {
            return this.localInfo.id;
        },
        userUniqueId() {
            return this.localInfo.uniqueId;
        },
        email() {
            return this.$store.getters.user.emailAddress;
        },
    },
    watch: {
        localInfo: {
            handler: function(newValue) {
                this.$emit('update:info', newValue);
            },
            deep: true,
            immediate: false
        },
        "v$.localInfo.$error": {
            handler: function(newValue){
                this.$emit('update:is-valid', !newValue);
            }
        }
    },
    validations: function() {
        var result = {
            localInfo: {
                cellPhone: {
                    required,
                    validFormat (value) {
                        if (_.isNullOrEmpty(value)) return true;
                        return RegEx.Phone.test(value);
                    },
                },
                mfaPhoneNumber: {
                    validFormat (value) {
                        if (_.isNullOrEmpty(value)) return true;
                        return RegEx.Phone.test(value);
                    },
                },
            }
        }
        return result;
    },
    created() {
        this.init();
    },
    methods: {
        init() {
            var item = _.merge({}, this.info);
            // If we enable HTMLEmail toggling, comment the following line out.
            item = _.merge({}, item, { htmlEmail: true });

            this.localInfo = new UserAccountProfile(item);
            this.refreshAvatar();
        },
        validate() {
            this.v$.$touch();
        },
        refreshAvatar() {
            const self = this;

            // console.log("refresh avatar");

            if (!self.userUniqueId) {
                self.avatar = null;
                return;
            }

            self.$api.UserAccountApi
                .getAvatar(self.userUniqueId)
                .then((response) => {
                    self.avatar = response;
                })
                .catch((error) => {
                    self.avatar = null;
                });
        },
        toggleAvatarPopup() {
            this.avatarPopupOpen = !this.avatarPopupOpen;
        },
        togglePasswordChangePopup() {
            this.passwordChangePopupOpen = !this.passwordChangePopupOpen;
        },
        onUpdateAvatar(avatarUpdate) {
            const self = this;

            const formData = new FormData();

            formData.append("file", avatarUpdate.blob);

            self.$api.UserAccountApi.saveAvatar(self.userUniqueId, formData)
                .then((response) => {
                    self.avatar = avatarUpdate.dataUrl;
                    self.$toast.success("Profile picture successfully updated.");
                })
                .catch((error) => {
                    let errMsg = error.message ? error.message : "A problem occurred while updating your profile picture. Please try again.";
                    self.$toast.error({ message: errMsg });
                })
        },
        removeAvatar() {
            const self = this;

            self.$api.UserAccountApi.removeAvatar(self.userUniqueId)
                .then((response) => {
                    self.avatar = "";
                    self.$toast.success("Profile picture successfully removed.");
                })
                .catch((error) => {
                    let errMsg = error.message ? error.message : "A problem occurred while removing your profile picture. Please try again.";
                    self.$toast.error({ message: errMsg });
                });
        },
        onFieldChange() {

        }
    }
}
</script>
<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss">
    #rq-profile-info {
        & section.rq-section .rq-section-header .rq-section-title{
            max-width: 95%;
        }
        & section.rq-section {
            border-bottom: 0;
        }
        & .custom-checkbox.rq-profile-email {
            display: inline-block;
            padding-right: 10px;
        }
        & .rq-selectbox-container {
            display: inline-block;
        }
    }
</style>